import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './ATMController.module.css';
import AtmTerminalHistory from './AtmTerminalHistory';
import {
    AtmTerminal,
    AtmBalance,
    getAtmTerminals,
    getRemoteConnectUrl,
} from '../../services/atmTerminals.service';
import { authService } from '../../services/auth.service';
import AtmManagersModal from './AtmManagersModal';

const REFRESH_INTERVAL_MS = 30_000;
const COUNT_UP_MS = 700;

type SortKey = 'status' | 'name' | 'shopBalance' | 'cashbox' | 'recycler';
type SortDir = 'asc' | 'desc';

const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Eases a number towards its new value so the KPI tiles roll rather than snap.
 * Honours the OS "reduce motion" setting by jumping straight to the value.
 */
const useCountUp = (value: number) => {
    const [display, setDisplay] = useState(value);
    const fromRef = useRef(value);
    const frameRef = useRef<number>();

    useEffect(() => {
        if (prefersReducedMotion()) {
            fromRef.current = value;
            setDisplay(value);
            return;
        }

        const from = fromRef.current;
        if (from === value) return;

        const start = performance.now();
        const tick = (now: number) => {
            const progress = Math.min((now - start) / COUNT_UP_MS, 1);
            // easeOutCubic
            const eased = 1 - Math.pow(1 - progress, 3);
            setDisplay(from + (value - from) * eased);
            if (progress < 1) {
                frameRef.current = requestAnimationFrame(tick);
            } else {
                fromRef.current = value;
            }
        };

        frameRef.current = requestAnimationFrame(tick);
        return () => {
            if (frameRef.current) cancelAnimationFrame(frameRef.current);
            fromRef.current = value;
        };
    }, [value]);

    return display;
};

const formatDateTime = (dateStr?: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const formatRelative = (dateStr?: string | null) => {
    if (!dateStr) return '—';
    const diffMs = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.round(diffMs / 60_000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.round(hours / 24)}d ago`;
};

const formatMoney = (value?: string | number | null) => {
    const num = Number(value ?? 0);
    if (!Number.isFinite(num)) return '$0.00';
    return num.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const formatGb = (mb?: number | null) => {
    if (mb === null || mb === undefined) return '—';
    return `${(mb / 1024).toFixed(1)} GB`;
};

const balanceTotal = (balance?: AtmBalance | null) => Number(balance?.total ?? 0);

const BillsBreakdown = ({ balance }: { balance?: AtmBalance | null }) => {
    const bills = Object.entries(balance?.bills || {})
        .filter(([, count]) => count > 0)
        .sort((a, b) => Number(b[0]) - Number(a[0]));

    if (bills.length === 0) {
        return <span className={styles.detailEmpty}>No notes</span>;
    }

    return (
        <div className={styles.billsBreakdown}>
            {bills.map(([denom, count]) => (
                <span key={denom} className={styles.billChip}>
                    <span className={styles.billDenom}>${denom}</span> × {count}
                </span>
            ))}
        </div>
    );
};

const StatCard = ({
    label,
    value,
    hint,
    accent,
    money = false,
    valueClass = '',
    index = 0,
}: {
    label: string;
    value: number;
    hint: string;
    accent: string;
    money?: boolean;
    valueClass?: string;
    index?: number;
}) => {
    const animated = useCountUp(value);

    return (
        <div
            className={`${styles.statCard} ${styles.enter}`}
            style={{ animationDelay: `${index * 70}ms` }}
        >
            <span className={styles.statAccent} data-accent={accent} />
            <span className={styles.statLabel}>{label}</span>
            <span className={`${styles.statValue} ${valueClass}`}>
                {money ? formatMoney(animated) : Math.round(animated)}
            </span>
            <span className={styles.statHint}>{hint}</span>
        </div>
    );
};

const StatusCell = ({ terminal }: { terminal: AtmTerminal }) => {
    if (!terminal.remote) {
        return (
            <span className={`${styles.statusPill} ${styles.statusUnlinked}`} title={`No Getscreen device matches remote ID "${terminal.remoteId}"`}>
                <span className={styles.statusDot} />
                Unlinked
            </span>
        );
    }

    const online = terminal.remote.online;
    return (
        <span
            className={`${styles.statusPill} ${online ? styles.statusOnline : styles.statusOffline}`}
            title={online ? 'Machine is reachable' : `Last seen ${formatRelative(terminal.remote.lastSeenAt)}`}
        >
            <span className={styles.statusDot} />
            {online ? 'Online' : 'Offline'}
        </span>
    );
};

const DetailPanel = ({ terminal }: { terminal: AtmTerminal }) => {
    const remote = terminal.remote;
    const hardware = remote?.hardware;

    return (
        <div className={styles.detailPanel}>
            <div className={styles.detailGrid}>
                <section className={`${styles.detailSection} ${styles.detailSectionWide}`}>
                    <h3 className={styles.detailTitle}>Cash in machine</h3>
                    <div className={styles.cashSplit}>
                        <div>
                            <div className={styles.cashLabel}>Recycler</div>
                            <div className={styles.detailTotal}>
                                {formatMoney(balanceTotal(terminal.balanceRecycler))}
                                <span className={styles.detailLimit}>
                                    limit {formatMoney(terminal.recyclerLimitBalance)}
                                </span>
                            </div>
                            <BillsBreakdown balance={terminal.balanceRecycler} />
                        </div>
                        <div>
                            <div className={styles.cashLabel}>Cashbox</div>
                            <div className={styles.detailTotal}>
                                {formatMoney(balanceTotal(terminal.balanceCashbox))}
                            </div>
                            <BillsBreakdown balance={terminal.balanceCashbox} />
                        </div>
                    </div>
                </section>

                <section className={styles.detailSection}>
                    <h3 className={styles.detailTitle}>Terminal</h3>
                    <dl className={styles.detailList}>
                        <dt>Terminal ID</dt>
                        <dd>#{terminal.id}</dd>
                        <dt>Shop user</dt>
                        <dd>{terminal.username ?? '—'} (#{terminal.userId})</dd>
                        <dt>Remote ID</dt>
                        <dd>{terminal.remoteId || '—'}</dd>
                        <dt>Address</dt>
                        <dd>{terminal.address || '—'}</dd>
                        <dt>Port</dt>
                        <dd>{terminal.port || '—'}</dd>
                        <dt>Phone for OTP</dt>
                        <dd>{terminal.phoneForOtp || '—'}</dd>
                        <dt>PIN code</dt>
                        <dd>{terminal.pinCode}</dd>
                        <dt>Created</dt>
                        <dd>{formatDateTime(terminal.createdAt)}</dd>
                        <dt>Updated</dt>
                        <dd>{formatDateTime(terminal.updatedAt)}</dd>
                    </dl>
                </section>

                <section className={`${styles.detailSection} ${styles.detailSectionWide}`}>
                    <h3 className={styles.detailTitle}>Machine</h3>
                    {remote ? (
                        <dl className={styles.detailList}>
                            <dt>Computer</dt>
                            <dd>{remote.machineName || '—'}</dd>
                            <dt>Windows user</dt>
                            <dd>{remote.osUser || '—'}</dd>
                            <dt>OS</dt>
                            <dd>
                                {hardware?.osName || remote.os || '—'}
                                {hardware?.osVersion ? ` (${hardware.osVersion})` : ''}
                            </dd>
                            <dt>Public IP</dt>
                            <dd>{remote.ip || '—'}</dd>
                            <dt>Location</dt>
                            <dd>
                                {[remote.city, remote.region, remote.country]
                                    .filter(Boolean)
                                    .join(', ') || '—'}
                            </dd>
                            <dt>Local IP</dt>
                            <dd>{hardware?.localIps?.[0] || '—'}</dd>
                            <dt>MAC</dt>
                            <dd>{hardware?.macAddress || '—'}</dd>
                            <dt>CPU</dt>
                            <dd>
                                {hardware?.cpu || '—'}
                                {hardware?.cpuCores ? ` · ${hardware.cpuCores} cores` : ''}
                            </dd>
                            <dt>RAM</dt>
                            <dd>
                                {hardware?.ramTotalMb
                                    ? `${formatGb(hardware.ramAvailableMb)} free of ${formatGb(hardware.ramTotalMb)}`
                                    : '—'}
                            </dd>
                            <dt>Disk</dt>
                            <dd>
                                {hardware?.disks?.length
                                    ? hardware.disks
                                          .map(
                                              (disk) =>
                                                  `${disk.name ?? '?'} ${formatGb(disk.freeMb)} free of ${formatGb(disk.sizeMb)}`,
                                          )
                                          .join(' · ')
                                    : '—'}
                            </dd>
                            <dt>On mains power</dt>
                            <dd>{remote.powerSupply === null ? '—' : remote.powerSupply ? 'Yes' : 'No (battery)'}</dd>
                            <dt>Getscreen agent</dt>
                            <dd>
                                v{remote.agentVersion || '?'} · id {remote.agentId}
                                {remote.deviceId ? ` · device ${remote.deviceId}` : ''}
                            </dd>
                            <dt>Total sessions</dt>
                            <dd>{remote.sessionsTotal ?? '—'}</dd>
                            <dt>Last seen</dt>
                            <dd>
                                {formatDateTime(remote.lastSeenAt)}{' '}
                                <span className={styles.detailMuted}>
                                    ({formatRelative(remote.lastSeenAt)})
                                </span>
                            </dd>
                        </dl>
                    ) : (
                        <p className={styles.detailEmpty}>
                            No Getscreen device matches remote ID{' '}
                            <strong>{terminal.remoteId || '(empty)'}</strong>. Set the
                            terminal's Remote ID to the machine's Getscreen agent id,
                            device id, or permanent-link code.
                        </p>
                    )}
                </section>

                <section className={`${styles.detailSection} ${styles.detailSectionWide}`}>
                    <h3 className={styles.detailTitle}>History</h3>
                    <AtmTerminalHistory atmId={terminal.userId} />
                </section>

                {terminal.remote?.permanentUrl && (
                    <section className={styles.detailSection}>
                        <h3 className={styles.detailTitle}>Permanent link</h3>
                        <a
                            className={styles.detailLink}
                            href={terminal.remote.permanentUrl}
                            target="_blank"
                            rel="noreferrer noopener"
                        >
                            {terminal.remote.permanentUrl}
                        </a>
                        <p className={styles.detailMuted}>
                            Asks for the machine's password. Use Connect for a
                            one-click session instead.
                        </p>
                    </section>
                )}
            </div>
        </div>
    );
};

const ATMController = () => {
    const [terminals, setTerminals] = useState<AtmTerminal[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [expandedId, setExpandedId] = useState<number | null>(null);
    const [connectingId, setConnectingId] = useState<number | null>(null);
    const [connectError, setConnectError] = useState<{ id: number; message: string } | null>(null);
    const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
    const [sortKey, setSortKey] = useState<SortKey>('status');
    const [sortDir, setSortDir] = useState<SortDir>('asc');
    // Rows animate in once, on first paint - not on every 30s poll.
    const [introDone, setIntroDone] = useState(false);
    const [showManagers, setShowManagers] = useState(false);
    const isAdmin = authService.getStoredUser()?.role.name === 'ADMIN';

    // Kept in a ref so the polling effect never needs to re-subscribe.
    const isFetchingRef = useRef(false);

    const fetchTerminals = useCallback((mode: 'initial' | 'refresh') => {
        if (isFetchingRef.current) return;
        isFetchingRef.current = true;

        if (mode === 'initial') {
            setLoading(true);
        } else {
            setRefreshing(true);
        }

        getAtmTerminals()
            .then((data) => {
                setTerminals(data);
                setLastUpdated(new Date());
                setLoadError(null);
            })
            .catch((error) => {
                console.error(error);
                setLoadError(error?.message || 'Failed to load ATM terminals');
            })
            .finally(() => {
                isFetchingRef.current = false;
                setLoading(false);
                setRefreshing(false);
            });
    }, []);

    useEffect(() => {
        fetchTerminals('initial');
    }, [fetchTerminals]);

    useEffect(() => {
        const timer = setInterval(() => fetchTerminals('refresh'), REFRESH_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [fetchTerminals]);

    useEffect(() => {
        if (loading) return;
        const timer = setTimeout(() => setIntroDone(true), 1400);
        return () => clearTimeout(timer);
    }, [loading]);

    const handleConnect = async (terminal: AtmTerminal) => {
        setConnectError(null);
        setConnectingId(terminal.id);

        // Opened up front: browsers only honour window.open outside of an async
        // gap when it is tied to the click that started it.
        const popup = window.open('', '_blank');

        try {
            const url = await getRemoteConnectUrl(terminal.id);
            if (popup) {
                popup.location.href = url;
            } else {
                window.location.href = url;
            }
        } catch (error: any) {
            popup?.close();
            const message =
                error?.graphQLErrors?.[0]?.message ||
                error?.message ||
                'Could not open a remote session';
            setConnectError({ id: terminal.id, message });
        } finally {
            setConnectingId(null);
        }
    };

    const toggleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key);
            setSortDir('asc');
        }
    };

    const visibleTerminals = useMemo(() => {
        const query = search.trim().toLowerCase();
        const matched = query
            ? terminals.filter((terminal) =>
                  [
                      terminal.username,
                      terminal.remoteId,
                      terminal.address,
                      terminal.phoneForOtp,
                      terminal.remote?.machineName,
                      terminal.remote?.ip,
                      terminal.remote?.city,
                      String(terminal.id),
                  ]
                      .filter(Boolean)
                      .some((value) => String(value).toLowerCase().includes(query)),
              )
            : terminals;

        // Rank for the status sort: reachable first, then offline, then unlinked.
        const statusRank = (terminal: AtmTerminal) => {
            if (!terminal.remote) return 2;
            return terminal.remote.online ? 0 : 1;
        };

        const compare = (a: AtmTerminal, b: AtmTerminal) => {
            switch (sortKey) {
                case 'status':
                    return statusRank(a) - statusRank(b);
                case 'name':
                    return (a.username ?? '').localeCompare(b.username ?? '');
                case 'shopBalance':
                    return (
                        Number(a.shopBalance?.amount ?? 0) -
                        Number(b.shopBalance?.amount ?? 0)
                    );
                case 'cashbox':
                    return balanceTotal(a.balanceCashbox) - balanceTotal(b.balanceCashbox);
                case 'recycler':
                    return balanceTotal(a.balanceRecycler) - balanceTotal(b.balanceRecycler);
                default:
                    return 0;
            }
        };

        return [...matched].sort((a, b) => {
            const result = compare(a, b);
            return sortDir === 'asc' ? result : -result;
        });
    }, [terminals, search, sortKey, sortDir]);

    const summary = useMemo(() => {
        const online = terminals.filter((t) => t.remote?.online).length;
        const offline = terminals.filter((t) => t.remote && !t.remote.online).length;
        const unlinked = terminals.filter((t) => !t.remote).length;
        const cash = terminals.reduce(
            (total, t) =>
                total + balanceTotal(t.balanceCashbox) + balanceTotal(t.balanceRecycler),
            0,
        );
        const shopFunds = terminals.reduce(
            (total, t) => total + Number(t.shopBalance?.amount ?? 0),
            0,
        );
        return { online, offline, unlinked, cash, shopFunds };
    }, [terminals]);

    const sortIndicator = (key: SortKey) =>
        sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    return (
        <main className={styles.main}>
            <div className={styles.headerRow}>
                <div>
                    <h1 className={styles.title}>ATM Controller</h1>
                    <div className={styles.subtitle}>
                        {terminals.length} terminal{terminals.length === 1 ? '' : 's'}
                        {lastUpdated && (
                            <>
                                {' · updated '}
                                {formatRelative(lastUpdated.toISOString())}
                                <span className={styles.liveDot} title="Auto-refreshes every 30s" />
                            </>
                        )}
                    </div>
                </div>
                <div className={styles.headerActions}>
                    <button
                        type="button"
                        className={styles.refreshBtn}
                        onClick={() => fetchTerminals('refresh')}
                        disabled={refreshing || loading}
                    >
                        <span className={refreshing ? styles.spinning : undefined}>⟳</span>
                        {refreshing ? 'Refreshing' : 'Refresh'}
                    </button>
                    {isAdmin && (
                        <button
                            type="button"
                            className={styles.refreshBtn}
                            onClick={() => setShowManagers(true)}
                        >
                            ATM managers
                        </button>
                    )}
                    <Link to="/atm-controller/create" className={styles.addBtn}>
                        + Add ATM Terminal
                    </Link>
                </div>
            </div>

            <div className={styles.statRow}>
                <StatCard
                    index={0}
                    accent="online"
                    label="Online"
                    value={summary.online}
                    hint="reachable now"
                    valueClass={styles.statValueOnline}
                />
                <StatCard
                    index={1}
                    accent="offline"
                    label="Offline"
                    value={summary.offline}
                    hint={
                        summary.unlinked > 0
                            ? `${summary.unlinked} unlinked`
                            : 'all linked'
                    }
                    valueClass={summary.offline > 0 ? styles.statValueOffline : ''}
                />
                <StatCard
                    index={2}
                    accent="cash"
                    label="Cash in machines"
                    value={summary.cash}
                    hint="cashbox + recycler"
                    money
                />
                <StatCard
                    index={3}
                    accent="funds"
                    label="Shop funds"
                    value={summary.shopFunds}
                    hint="wallet balances"
                    money
                />
            </div>

            <div className={styles.toolbar}>
                <div className={styles.searchRow}>
                    <svg
                        className={styles.searchIcon}
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        aria-hidden="true"
                    >
                        <circle cx="11" cy="11" r="7" />
                        <path d="M20 20l-3.5-3.5" />
                    </svg>
                    <input
                        type="text"
                        className={styles.searchInput}
                        placeholder="Search by shop, remote ID, address, machine, IP…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    {search && (
                        <button
                            type="button"
                            className={styles.clearSearchBtn}
                            onClick={() => setSearch('')}
                            aria-label="Clear search"
                        >
                            ×
                        </button>
                    )}
                </div>
                <span className={styles.resultCount}>
                    {visibleTerminals.length} of {terminals.length} shown
                </span>
            </div>

            {loadError && <div className={styles.errorBanner}>{loadError}</div>}

            {loading ? (
                <div className={styles.loading}>Loading terminals...</div>
            ) : terminals.length === 0 ? (
                <div className={styles.noData}>No ATM terminals found</div>
            ) : visibleTerminals.length === 0 ? (
                <div className={styles.noData}>No terminals match "{search}"</div>
            ) : (
                <div className={styles.tableWrapper}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th className={styles.sortable} onClick={() => toggleSort('status')}>
                                    Status{sortIndicator('status')}
                                </th>
                                <th className={styles.sortable} onClick={() => toggleSort('name')}>
                                    ATM / Shop{sortIndicator('name')}
                                </th>
                                <th>Address</th>
                                <th>Phone / PIN</th>
                                <th className={styles.sortable} onClick={() => toggleSort('shopBalance')}>
                                    Shop balance{sortIndicator('shopBalance')}
                                </th>
                                <th className={styles.sortable} onClick={() => toggleSort('cashbox')}>
                                    Cashbox{sortIndicator('cashbox')}
                                </th>
                                <th className={styles.sortable} onClick={() => toggleSort('recycler')}>
                                    Recycler{sortIndicator('recycler')}
                                </th>
                                <th>Last seen</th>
                                <th className={styles.actionsHeader}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleTerminals.map((terminal, rowIndex) => {
                                const isExpanded = expandedId === terminal.id;
                                const recyclerTotal = balanceTotal(terminal.balanceRecycler);
                                const recyclerLimit = Number(terminal.recyclerLimitBalance ?? 0);
                                const overLimit =
                                    recyclerLimit > 0 && recyclerTotal >= recyclerLimit;

                                return (
                                    <Fragment key={terminal.id}>
                                        <tr
                                            style={
                                                introDone
                                                    ? undefined
                                                    : { animationDelay: `${140 + rowIndex * 55}ms` }
                                            }
                                            className={[
                                                isExpanded ? styles.selectedRow : '',
                                                introDone ? '' : styles.enterRow,
                                            ]
                                                .filter(Boolean)
                                                .join(' ')}
                                            onClick={() =>
                                                setExpandedId(isExpanded ? null : terminal.id)
                                            }
                                        >
                                            <td>
                                                <StatusCell terminal={terminal} />
                                            </td>
                                            <td className={styles.atmCell}>
                                                <div className={styles.atmName}>
                                                    <span className={styles.expandCaret}>
                                                        {isExpanded ? '▾' : '▸'}
                                                    </span>
                                                    {terminal.username || `ATM #${terminal.id}`}
                                                </div>
                                                <div
                                                    className={`${styles.atmSub} ${styles.truncate}`}
                                                    title={`${terminal.remoteId || 'no remote ID'}${terminal.remote?.machineName ? ` · ${terminal.remote.machineName}` : ''}`}
                                                >
                                                    {terminal.remoteId || 'no remote ID'}
                                                    {terminal.remote?.machineName
                                                        ? ` · ${terminal.remote.machineName}`
                                                        : ''}
                                                </div>
                                            </td>
                                            <td className={styles.addressCell}>
                                                {terminal.address || '—'}
                                                {terminal.remote?.city && (
                                                    <div className={styles.atmSub}>
                                                        {terminal.remote.city}
                                                    </div>
                                                )}
                                            </td>
                                            <td className={styles.phoneCell}>
                                                <div title={terminal.phoneForOtp}>
                                                    {terminal.phoneForOtp || '—'}
                                                </div>
                                                <div className={styles.atmSub}>
                                                    PIN {terminal.pinCode}
                                                </div>
                                            </td>
                                            <td className={styles.amount}>
                                                {terminal.shopBalance
                                                    ? formatMoney(terminal.shopBalance.amount)
                                                    : '—'}
                                                {Number(terminal.shopBalance?.credit ?? 0) > 0 && (
                                                    <div className={styles.atmSub}>
                                                        +{formatMoney(terminal.shopBalance?.credit)}{' '}
                                                        credit
                                                    </div>
                                                )}
                                            </td>
                                            <td className={styles.amount}>
                                                {formatMoney(balanceTotal(terminal.balanceCashbox))}
                                            </td>
                                            <td className={styles.amount}>
                                                <span className={overLimit ? styles.overLimit : undefined}>
                                                    {formatMoney(recyclerTotal)}
                                                </span>
                                                <div className={styles.atmSub}>
                                                    limit {formatMoney(recyclerLimit)}
                                                </div>
                                            </td>
                                            <td className={styles.nowrap}>
                                                {!terminal.remote ? (
                                                    '—'
                                                ) : terminal.remote.online ? (
                                                    <span className={styles.seenNow}>now</span>
                                                ) : (
                                                    formatRelative(terminal.remote.lastSeenAt)
                                                )}
                                            </td>
                                            <td
                                                className={styles.actionsCell}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <button
                                                    type="button"
                                                    className={styles.connectBtn}
                                                    disabled={
                                                        !terminal.remote?.canConnect ||
                                                        connectingId === terminal.id
                                                    }
                                                    title={
                                                        !terminal.remote
                                                            ? 'No Getscreen device linked to this remote ID'
                                                            : !terminal.remote.canConnect
                                                              ? 'Machine is offline'
                                                              : 'Open a remote session'
                                                    }
                                                    onClick={() => handleConnect(terminal)}
                                                >
                                                    {connectingId === terminal.id
                                                        ? 'Opening…'
                                                        : 'Connect'}
                                                </button>
                                                <Link
                                                    to={`/atm-controller/${terminal.id}`}
                                                    className={styles.editBtn}
                                                >
                                                    Edit
                                                </Link>
                                            </td>
                                        </tr>
                                        {connectError?.id === terminal.id && (
                                            <tr className={styles.errorRow}>
                                                <td colSpan={9}>{connectError.message}</td>
                                            </tr>
                                        )}
                                        {isExpanded && (
                                            <tr className={styles.detailRow}>
                                                <td colSpan={9}>
                                                    <DetailPanel terminal={terminal} />
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {showManagers && <AtmManagersModal onClose={() => setShowManagers(false)} />}
        </main>
    );
};

export default ATMController;
